import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchJson } from "../../lib/api";
import { todayIST } from "../../lib/time";

interface HeatmapCell {
  date: string;
  count: number;
}

interface HeatmapResponse {
  year: number;
  cells: HeatmapCell[];
}

export function ContributionHeatmap() {
  const currentYear = new Date().getUTCFullYear();
  const today = todayIST();

  const { data, isLoading } = useQuery({
    queryKey: ["heatmap", currentYear],
    queryFn: () => fetchJson<HeatmapResponse>(`/api/stats/heatmap?year=${currentYear}`),
    staleTime: 5 * 60 * 1000,
  });

  const cellsMap = useMemo(() => {
    const map = new Map<string, number>();
    if (data?.cells) {
      for (const cell of data.cells) {
        map.set(cell.date, cell.count);
      }
    }
    return map;
  }, [data]);

  const cellsTotalMap = useMemo(() => {
    const map = new Map<string, number>();
    if (data?.cells) {
      for (const cell of data.cells) {
        const cellAny = cell as any;
        map.set(cell.date, cellAny.total ?? 1);
      }
    }
    return map;
  }, [data]);

  // Generate a grid of 53 weeks (columns) x 7 days (rows)
  const grid = useMemo(() => {
    // Start at January 1st of the current year (e.g. 2026)
    const jan1 = new Date(`${currentYear}-01-01T00:00:00Z`);
    // Find the Sunday of the week containing January 1st
    const startDay = jan1.getUTCDay(); // 0 is Sunday, 1 is Monday...
    const sunday = new Date(jan1.getTime() - startDay * 24 * 3600 * 1000);

    const cols: Array<Array<{ dateStr: string; count: number; total: number; dayLabel: number }>> = [];
    let current = new Date(sunday.getTime());

    // We build 53 columns to cover the whole year
    for (let w = 0; w < 53; w++) {
      const colDays: Array<{ dateStr: string; count: number; total: number; dayLabel: number }> = [];
      for (let d = 0; d < 7; d++) {
        const iso = current.toISOString().slice(0, 10);
        const count = cellsMap.get(iso) ?? 0;
        const total = cellsTotalMap.get(iso) ?? 1;
        colDays.push({
          dateStr: iso,
          count,
          total,
          dayLabel: current.getUTCDate(),
        });
        current = new Date(current.getTime() + 24 * 3600 * 1000);
      }
      cols.push(colDays);
    }
    return cols;
  }, [cellsMap, cellsTotalMap, currentYear]);

  // Determine month labels and their column span indices
  const monthLabels = useMemo(() => {
    const labels: Array<{ text: string; colIndex: number }> = [];
    let lastMonth = -1;

    grid.forEach((col, idx) => {
      // Look at the Wednesday (middle day) of the week to check month
      const wednesdayDateStr = col[3]?.dateStr;
      if (wednesdayDateStr) {
        const m = new Date(`${wednesdayDateStr}T00:00:00Z`).getUTCMonth();
        if (m !== lastMonth) {
          const name = new Date(`${wednesdayDateStr}T00:00:00Z`).toLocaleString("default", {
            month: "short",
            timeZone: "UTC",
          });
          labels.push({ text: name, colIndex: idx });
          lastMonth = m;
        }
      }
    });

    return labels;
  }, [grid]);

  if (isLoading) {
    return <div style={{ fontSize: "0.85rem", color: "var(--fg-dim)" }}>$ loading contribution heatmap...</div>;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.25rem",
        border: "1px solid var(--border)",
        padding: "0.75rem",
        backgroundColor: "var(--bg-elevated)",
        overflowX: "auto",
        width: "100%",
        fontFamily: "JetBrains Mono, monospace",
      }}
    >
      <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)", marginBottom: "0.25rem" }}>
        &gt; annual contributions heatmap ({currentYear})
      </div>

      <div style={{ display: "flex", gap: "0.5rem" }}>
        {/* Row labels */}
        <div
          style={{
            display: "grid",
            gridTemplateRows: "repeat(7, 10px)",
            gap: "2px",
            fontSize: "0.5rem",
            color: "var(--fg-dim)",
            justifyContent: "end",
            alignContent: "center",
            paddingTop: "15px", // offset for month labels
          }}
        >
          <div></div> {/* Sun */}
          <div>Mon</div>
          <div></div> {/* Tue */}
          <div>Wed</div>
          <div></div> {/* Thu */}
          <div>Fri</div>
          <div></div> {/* Sat */}
        </div>

        {/* Columns grid */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
          {/* Month labels header */}
          <div style={{ display: "flex", height: "12px", position: "relative", fontSize: "0.55rem", color: "var(--fg-dim)" }}>
            {monthLabels.map((lbl, idx) => (
              <span
                key={idx}
                style={{
                  position: "absolute",
                  left: `${lbl.colIndex * 12}px`,
                  whiteSpace: "nowrap",
                }}
              >
                {lbl.text}
              </span>
            ))}
          </div>

          {/* Grid rows */}
          <div
            style={{
              display: "grid",
              gridAutoFlow: "column",
              gridTemplateColumns: "repeat(53, 10px)",
              gridTemplateRows: "repeat(7, 10px)",
              gap: "2px",
            }}
          >
            {grid.map((col, colIdx) =>
              col.map((day, dayIdx) => {
                const isFuture = new Date(`${day.dateStr}T00:00:00Z`).getTime() > new Date(`${today}T00:00:00Z`).getTime();
                
                // Color-mix based on completion rate
                const effectiveCount = isFuture ? 0 : day.count;
                const effectiveTotal = isFuture ? 1 : day.total;
                const percent = effectiveTotal > 0 ? effectiveCount / effectiveTotal : 0;
                
                let cellBg = "color-mix(in srgb, var(--accent) 8%, var(--bg-elevated))";
                if (effectiveCount > 0) {
                  if (percent <= 0.25) cellBg = "color-mix(in srgb, var(--accent) 30%, var(--bg-elevated))";
                  else if (percent <= 0.50) cellBg = "color-mix(in srgb, var(--accent) 55%, var(--bg-elevated))";
                  else if (percent <= 0.75) cellBg = "color-mix(in srgb, var(--accent) 80%, var(--bg-elevated))";
                  else cellBg = "var(--accent)";
                }

                return (
                  <div
                    key={`${colIdx}-${dayIdx}`}
                    title={isFuture ? "Future date" : `${day.dateStr}: ${day.count}/${day.total} completed (${Math.round(percent * 100)}%)`}
                    style={{
                      width: "10px",
                      height: "10px",
                      backgroundColor: cellBg,
                      border: "1px solid var(--border)",
                      transition: "background-color 0.1s ease",
                    }}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "0.55rem",
          color: "var(--fg-dim)",
          marginTop: "0.25rem",
          paddingLeft: "2.5rem",
        }}
      >
        <span>Completion rates (done / total tasks)</span>
        <div style={{ display: "flex", gap: "2px", alignItems: "center" }}>
          <span>Less</span>
          <div style={{ width: "8px", height: "8px", backgroundColor: "color-mix(in srgb, var(--accent) 8%, var(--bg-elevated))", border: "1px solid var(--border)" }} />
          <div style={{ width: "8px", height: "8px", backgroundColor: "color-mix(in srgb, var(--accent) 30%, var(--bg-elevated))", border: "1px solid var(--border)" }} />
          <div style={{ width: "8px", height: "8px", backgroundColor: "color-mix(in srgb, var(--accent) 55%, var(--bg-elevated))", border: "1px solid var(--border)" }} />
          <div style={{ width: "8px", height: "8px", backgroundColor: "color-mix(in srgb, var(--accent) 80%, var(--bg-elevated))", border: "1px solid var(--border)" }} />
          <div style={{ width: "8px", height: "8px", backgroundColor: "var(--accent)", border: "1px solid var(--border)" }} />
          <span>More</span>
        </div>
      </div>
    </div>
  );
}
